/**
 * WI-B-07 — the dev stub AuthKit driver. Behind VITE_MARK_AUTH_STUB, replaces
 * the live hosted-AuthKit round-trip with a local fixture "magic link" so the
 * whole login flow is clickable under `npm run dev` with no live WorkOS.
 *
 * It stands up a MockWorkos directory (one Mercer org, a few seeded users at
 * each role) and implements the AuthDriver seam AuthGate expects. The email the
 * dev "types" selects which seeded user logs in, exercising every route:
 *   admin@mercer.test      → admin,      has projects   → shell
 *   super@mercer.test      → supervisor, has projects   → shell (basic UI tier)
 *   member@mercer.test     → member,     has projects   → shell
 *   nobody@mercer.test     → member,     NO projects     → A6
 *   ghost@nowhere.test     → no membership               → A5 denied
 *
 * NONE of this ships to production: the live driver (redirect to authorize_url)
 * is selected when the flag is unset. Live leg is §S8, not built here.
 */

import { MockWorkos } from "../auth/workos-mock";
import type { AuthDriver } from "../components/AuthGate";
import type { SessionScope } from "../auth/workos-session";
import { verifyAndMapWorkosToken } from "../auth/workos-identity";

const MERCER = "org_mercer_pilot";
const TENANT = "tenant-mercer";

interface SeedUser {
  email: string;
  user_id: string;
  role: "member" | "supervisor" | "admin";
  project_ids: string[];
}

const SEEDS: SeedUser[] = [
  { email: "admin@mercer.test", user_id: "u_admin", role: "admin", project_ids: ["Fray", "Foster"] },
  { email: "super@mercer.test", user_id: "u_super", role: "supervisor", project_ids: ["Fray"] },
  { email: "member@mercer.test", user_id: "u_member", role: "member", project_ids: ["Fray"] },
  { email: "nobody@mercer.test", user_id: "u_nobody", role: "member", project_ids: [] },
];

/** Build the stub driver. `emailProvider` supplies the "typed" email — in the
 * running app a prompt collects it; tests inject it directly. `queued` supplies
 * the queued-capture count for the A5 copy. */
export function makeStubAuthDriver(
  emailProvider: () => string,
  queued: () => Promise<number> = async () => 0,
): AuthDriver {
  const wk = new MockWorkos();
  wk.createOrg(MERCER, TENANT);
  for (const s of SEEDS) {
    wk.createMembership(s.user_id, MERCER, s.role);
  }
  const byEmail = new Map(SEEDS.map((s) => [s.email, s]));

  return {
    async beginLogin() {
      const email = emailProvider().trim().toLowerCase();
      const seed = byEmail.get(email);
      // Unknown email → no membership → the code carries a ghost user, whose
      // exchange returns null → A5 denied. Mirrors the real "not provisioned".
      const user_id = seed?.user_id ?? "u_ghost";
      return { code: `stub:${user_id}` };
    },
    exchange: async (code: string) => {
      const user_id = code.replace(/^stub:/, "");
      return wk.authkitLogin(user_id, MERCER); // null if no membership
    },
    fetchScope: async (user_id: string, org_id: string): Promise<SessionScope> => {
      const seed = SEEDS.find((s) => s.user_id === user_id);
      // Bearer is a fixture string; the live leg (§S8) fetches a real scoped one.
      return {
        tenant_id: TENANT,
        project_ids: seed?.project_ids ?? [],
        bearer: `stub-bearer-${user_id}-${org_id}`,
      };
    },
    checkMembership: (user_id: string, org_id: string) => {
      // Bind the token's org/role to the real mock directory: a token claiming
      // an org the user isn't a member of (or a role above their membership) is
      // rejected in establishSession (FIX-B1 cross-org isolation).
      const m = wk.membershipOf(user_id, org_id);
      return m ? { role: m.role } : null;
    },
    queuedCount: queued,
  };
}

/** True when the dev stub is enabled. Reads the Vite env flag; defaults OFF so
 * production never picks the stub. */
export function isAuthStubEnabled(): boolean {
  // import.meta.env is Vite's build-time env. In test/node it may be undefined.
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
  return env?.VITE_MARK_AUTH_STUB === "1" || env?.VITE_MARK_AUTH_STUB === "true";
}

/** Re-export for the stub landing banner: verify a stub token (dev only). */
export { verifyAndMapWorkosToken };
